import React, { useEffect, useMemo, useState, useContext } from "react"
import axios from "axios"
import { useOutletContext } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  FaTicketAlt, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff,
  FaTimes, FaSave, FaCalendarAlt, FaTag, FaCheckCircle
} from "react-icons/fa"
import { UserContext } from "../../context/UserContext"
import "../../styles/circuitoDesignSystem.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

const formVazio = {
  codigo: "",
  descricao: "",
  loteDestino: 1,
  todasCategorias: true,
  categorias: [],
  validoDe: "",
  validoAte: "",
  ativo: true,
}

function paraInputDateTime(valor) {
  if (!valor) return ""
  const d = new Date(valor)
  if (Number.isNaN(d.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatarData(valor) {
  if (!valor) return "Sem limite"
  const d = new Date(valor)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })
}

export default function CircuitoCuponsPainel() {
  const { circuito } = useOutletContext()
  const { token: tokenCtx } = useContext(UserContext)
  const token = tokenCtx || localStorage.getItem("token")

  const [cupons, setCupons] = useState([])
  const [loading, setLoading] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editandoId, setEditandoId] = useState(null)
  const [form, setForm] = useState(formVazio)
  const [erro, setErro] = useState("")

  const categoriasCircuito = useMemo(
    () => (circuito?.categorias || []).map((c) => c?.nome || c).filter(Boolean),
    [circuito]
  )

  const headers = { Authorization: `Bearer ${token}` }

  const buscarCupons = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${API}/circuitos/${circuito._id}/cupons`, { headers })
      setCupons(res.data?.cupons || [])
    } catch (e) {
      setErro(e?.response?.data?.erro || "Não foi possível carregar os cupons.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (circuito?._id) buscarCupons()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circuito?._id])

  const abrirNovo = () => {
    setEditandoId(null)
    setForm(formVazio)
    setErro("")
    setModalOpen(true)
  }

  const abrirEdicao = (cupom) => {
    setEditandoId(cupom._id)
    setForm({
      codigo: cupom.codigo || "",
      descricao: cupom.descricao || "",
      loteDestino: Number(cupom.loteDestino || 1),
      todasCategorias: !cupom.categorias?.length,
      categorias: cupom.categorias || [],
      validoDe: paraInputDateTime(cupom.validoDe),
      validoAte: paraInputDateTime(cupom.validoAte),
      ativo: cupom.ativo !== false,
    })
    setErro("")
    setModalOpen(true)
  }

  const fecharModal = () => {
    if (salvando) return
    setModalOpen(false)
    setErro("")
  }

  const toggleCategoria = (nome) => {
    setForm((atual) => {
      const existe = atual.categorias.includes(nome)
      return {
        ...atual,
        categorias: existe
          ? atual.categorias.filter((c) => c !== nome)
          : [...atual.categorias, nome],
      }
    })
  }

  const salvar = async (e) => {
    e.preventDefault()
    const codigo = form.codigo.trim().toUpperCase().replace(/\s/g, "")
    if (!codigo) return setErro("Informe o código do cupom.")

    const payload = {
      codigo,
      descricao: form.descricao.trim(),
      loteDestino: Number(form.loteDestino),
      categorias: form.todasCategorias ? [] : form.categorias,
      validoDe: form.validoDe || null,
      validoAte: form.validoAte || null,
      ativo: form.ativo,
    }

    if (!form.todasCategorias && payload.categorias.length === 0) {
      return setErro("Selecione pelo menos uma categoria ou marque “Todas as categorias”.")
    }

    try {
      setSalvando(true)
      setErro("")
      if (editandoId) {
        await axios.patch(`${API}/circuitos/${circuito._id}/cupons/${editandoId}`, payload, { headers })
      } else {
        await axios.post(`${API}/circuitos/${circuito._id}/cupons`, payload, { headers })
      }
      setModalOpen(false)
      await buscarCupons()
    } catch (e) {
      setErro(e?.response?.data?.erro || "Não foi possível salvar o cupom.")
    } finally {
      setSalvando(false)
    }
  }

  const alternarAtivo = async (cupom) => {
    try {
      setErro("")
      await axios.patch(
        `${API}/circuitos/${circuito._id}/cupons/${cupom._id}`,
        { ativo: !cupom.ativo },
        { headers }
      )
      setCupons((lista) => lista.map((item) =>
        item._id === cupom._id ? { ...item, ativo: !item.ativo } : item
      ))
    } catch (e) {
      setErro(e?.response?.data?.erro || "Não foi possível alterar o status do cupom.")
    }
  }

  const excluir = async (cupom) => {
    const ok = window.confirm(`Excluir definitivamente o cupom ${cupom.codigo}?`)
    if (!ok) return

    try {
      setErro("")
      await axios.delete(`${API}/circuitos/${circuito._id}/cupons/${cupom._id}`, { headers })
      setCupons((lista) => lista.filter((item) => item._id !== cupom._id))
    } catch (e) {
      setErro(e?.response?.data?.erro || "Não foi possível excluir o cupom.")
    }
  }

  const statusCupom = (cupom) => {
    const agora = new Date()
    if (!cupom.ativo) return { label: "Inativo", color: "#94a3b8", bg: "rgba(148,163,184,.12)" }
    if (cupom.validoDe && agora < new Date(cupom.validoDe)) return { label: "Agendado", color: "#38bdf8", bg: "rgba(56,189,248,.12)" }
    if (cupom.validoAte && agora > new Date(cupom.validoAte)) return { label: "Expirado", color: "#f87171", bg: "rgba(248,113,113,.12)" }
    return { label: "Ativo", color: "#34d399", bg: "rgba(52,211,153,.12)" }
  }

  return (
    <div style={{ paddingBottom: 42 }}>
      <div className="pc-page-header" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap"
      }}>
        <div>
          <h1 className="pc-page-header__title">Cupons promocionais</h1>
          <p className="pc-page-header__subtitle">
            Crie códigos que fazem a associação usar o valor oficial de um lote específico.
          </p>
        </div>

        <button
          type="button"
          onClick={abrirNovo}
          className="c-btn c-btn--primary"
          style={{ height: 48, display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}
        >
          <FaPlus /> Novo cupom
        </button>
      </div>

      <div style={{
        marginBottom: 24, padding: "16px 18px", borderRadius: 14,
        border: "1px solid rgba(212,175,55,.24)",
        background: "linear-gradient(135deg, rgba(212,175,55,.09), rgba(212,175,55,.025))",
        display: "flex", gap: 12, alignItems: "flex-start"
      }}>
        <FaTicketAlt color="var(--c-accent)" size={20} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <strong style={{ color: "#fff", fontSize: 14 }}>Como funciona</strong>
          <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,.58)", fontSize: 13, lineHeight: 1.55 }}>
            Exemplo: o código <strong style={{ color: "var(--c-accent)" }}>THAYUAN</strong> configurado para
            <strong style={{ color: "#fff" }}> 1º Lote</strong> faz o usuário pagar o valor original do 1º lote,
            mesmo que o circuito já esteja no 2º lote. A venda também entra no 1º lote do financeiro e do PDF.
          </p>
        </div>
      </div>

      {erro && !modalOpen && (
        <div style={{
          marginBottom: 18, padding: "12px 14px", borderRadius: 10,
          color: "#f87171", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)"
        }}>
          {erro}
        </div>
      )}

      <div className="pc-card" style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 42, textAlign: "center", color: "rgba(255,255,255,.55)" }}>
            Carregando cupons...
          </div>
        ) : cupons.length === 0 ? (
          <div style={{ padding: "54px 20px", textAlign: "center" }}>
            <div style={{
              width: 58, height: 58, margin: "0 auto 16px", borderRadius: 18,
              background: "rgba(212,175,55,.10)", color: "var(--c-accent)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <FaTicketAlt size={25} />
            </div>
            <h3 style={{ color: "#fff", margin: "0 0 7px", fontSize: 18 }}>Nenhum cupom criado</h3>
            <p style={{ color: "rgba(255,255,255,.5)", margin: 0, fontSize: 13 }}>
              Crie o primeiro código promocional do circuito.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="pc-table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Cupom</th>
                  <th>Benefício</th>
                  <th>Categorias</th>
                  <th>Validade</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {cupons.map((cupom) => {
                  const status = statusCupom(cupom)
                  return (
                    <tr key={cupom._id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{
                            width: 34, height: 34, borderRadius: 9,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: "rgba(212,175,55,.10)", color: "var(--c-accent)"
                          }}>
                            <FaTag size={14} />
                          </span>
                          <div>
                            <strong style={{ color: "#fff", letterSpacing: ".7px" }}>{cupom.codigo}</strong>
                            {cupom.descricao && (
                              <div style={{ color: "rgba(255,255,255,.45)", fontSize: 11, marginTop: 2 }}>
                                {cupom.descricao}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          padding: "6px 10px", borderRadius: 999,
                          background: Number(cupom.loteDestino) === 1 ? "rgba(16,185,129,.11)" : "rgba(245,158,11,.11)",
                          color: Number(cupom.loteDestino) === 1 ? "#34d399" : "#fbbf24",
                          fontSize: 12, fontWeight: 800
                        }}>
                          <FaCheckCircle size={11} />
                          {cupom.loteDestino}º Lote
                        </span>
                      </td>
                      <td style={{ maxWidth: 270 }}>
                        {!cupom.categorias?.length
                          ? <span style={{ color: "#fff", fontWeight: 700 }}>Todas as categorias</span>
                          : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {cupom.categorias.slice(0, 3).map((nome) => (
                                <span key={nome} style={{
                                  fontSize: 10, padding: "4px 7px", borderRadius: 7,
                                  background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.72)"
                                }}>{nome}</span>
                              ))}
                              {cupom.categorias.length > 3 && (
                                <span style={{ fontSize: 10, color: "rgba(255,255,255,.45)" }}>
                                  +{cupom.categorias.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, color: "rgba(255,255,255,.68)", fontSize: 12 }}>
                          <FaCalendarAlt size={11} color="rgba(255,255,255,.35)" />
                          <span>
                            {cupom.validoDe || cupom.validoAte
                              ? `${formatarData(cupom.validoDe)} → ${formatarData(cupom.validoAte)}`
                              : "Sem prazo"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          padding: "6px 10px", borderRadius: 999, fontSize: 11,
                          fontWeight: 900, textTransform: "uppercase",
                          color: status.color, background: status.bg
                        }}>
                          {status.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 7 }}>
                          <button
                            type="button"
                            onClick={() => alternarAtivo(cupom)}
                            title={cupom.ativo ? "Desativar" : "Ativar"}
                            style={actionButton}
                          >
                            {cupom.ativo ? <FaToggleOn color="#34d399" size={18} /> : <FaToggleOff size={18} />}
                          </button>
                          <button type="button" onClick={() => abrirEdicao(cupom)} title="Editar" style={actionButton}>
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => excluir(cupom)}
                            title="Excluir"
                            style={{ ...actionButton, color: "#f87171" }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalOpen && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 10000, padding: 16,
            background: "rgba(0,0,0,.78)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <motion.div
              initial={{ opacity: 0, y: 18, scale: .98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: .98 }}
              style={{
                width: "100%", maxWidth: 650, maxHeight: "92vh", overflowY: "auto",
                borderRadius: 20, background: "#121212",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "0 30px 80px rgba(0,0,0,.55)"
              }}
            >
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "22px 24px", borderBottom: "1px solid rgba(255,255,255,.07)"
              }}>
                <div>
                  <h2 style={{ margin: 0, color: "#fff", fontSize: 20 }}>
                    {editandoId ? "Editar cupom" : "Novo cupom"}
                  </h2>
                  <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,.45)", fontSize: 12 }}>
                    O valor aplicado será sempre o preço oficial do lote escolhido.
                  </p>
                </div>
                <button type="button" onClick={fecharModal} style={closeButton}><FaTimes /></button>
              </div>

              <form onSubmit={salvar} style={{ padding: 24 }}>
                {erro && (
                  <div style={{
                    marginBottom: 18, padding: "11px 13px", borderRadius: 10,
                    color: "#f87171", background: "rgba(239,68,68,.08)",
                    border: "1px solid rgba(239,68,68,.2)", fontSize: 13
                  }}>{erro}</div>
                )}

                <div style={grid2}>
                  <div>
                    <label style={labelStyle}>Código do cupom</label>
                    <input
                      className="c-input"
                      value={form.codigo}
                      maxLength={40}
                      onChange={(e) => setForm({
                        ...form,
                        codigo: e.target.value.toUpperCase().replace(/\s/g, "")
                      })}
                      placeholder="Ex: THAYUAN"
                      style={{ width: "100%", height: 46, padding: "0 14px", fontWeight: 900, letterSpacing: ".8px" }}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Preço que o cupom libera</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[1, 2].map((lote) => (
                        <button
                          key={lote}
                          type="button"
                          onClick={() => setForm({ ...form, loteDestino: lote })}
                          style={{
                            height: 46, borderRadius: 11, cursor: "pointer",
                            border: Number(form.loteDestino) === lote
                              ? "1px solid var(--c-accent)"
                              : "1px solid rgba(255,255,255,.10)",
                            background: Number(form.loteDestino) === lote
                              ? "rgba(212,175,55,.11)"
                              : "rgba(255,255,255,.025)",
                            color: Number(form.loteDestino) === lote ? "var(--c-accent)" : "rgba(255,255,255,.65)",
                            fontWeight: 800
                          }}
                        >
                          {lote}º Lote
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 17 }}>
                  <label style={labelStyle}>Descrição interna <span style={{ opacity: .45 }}>(opcional)</span></label>
                  <input
                    className="c-input"
                    value={form.descricao}
                    maxLength={160}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    placeholder="Ex: Promoção parceiros / divulgação"
                    style={{ width: "100%", height: 46, padding: "0 14px" }}
                  />
                </div>

                <div style={{ marginTop: 20, padding: 16, borderRadius: 14, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.todasCategorias}
                      onChange={(e) => setForm({
                        ...form,
                        todasCategorias: e.target.checked,
                        categorias: e.target.checked ? [] : form.categorias
                      })}
                      style={{ width: 18, height: 18, accentColor: "var(--c-accent)" }}
                    />
                    <div>
                      <strong style={{ color: "#fff", fontSize: 13 }}>Todas as categorias</strong>
                      <div style={{ color: "rgba(255,255,255,.43)", fontSize: 11, marginTop: 2 }}>
                        Desmarque para liberar o cupom apenas em categorias específicas.
                      </div>
                    </div>
                  </label>

                  {!form.todasCategorias && (
                    <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 7 }}>
                      {categoriasCircuito.map((nome) => {
                        const marcado = form.categorias.includes(nome)
                        return (
                          <button
                            type="button"
                            key={nome}
                            onClick={() => toggleCategoria(nome)}
                            style={{
                              padding: "8px 11px", borderRadius: 9, cursor: "pointer",
                              border: marcado ? "1px solid rgba(212,175,55,.55)" : "1px solid rgba(255,255,255,.09)",
                              background: marcado ? "rgba(212,175,55,.10)" : "rgba(255,255,255,.025)",
                              color: marcado ? "var(--c-accent)" : "rgba(255,255,255,.63)",
                              fontSize: 12, fontWeight: 700
                            }}
                          >
                            {nome}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div style={{ ...grid2, marginTop: 18 }}>
                  <div>
                    <label style={labelStyle}>Válido a partir de <span style={{ opacity: .45 }}>(opcional)</span></label>
                    <input
                      type="datetime-local"
                      className="c-input"
                      value={form.validoDe}
                      onChange={(e) => setForm({ ...form, validoDe: e.target.value })}
                      style={{ width: "100%", height: 46, padding: "0 12px", colorScheme: "dark" }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Válido até <span style={{ opacity: .45 }}>(opcional)</span></label>
                    <input
                      type="datetime-local"
                      className="c-input"
                      value={form.validoAte}
                      onChange={(e) => setForm({ ...form, validoAte: e.target.value })}
                      style={{ width: "100%", height: 46, padding: "0 12px", colorScheme: "dark" }}
                    />
                  </div>
                </div>

                <label style={{
                  marginTop: 18, display: "flex", alignItems: "center", gap: 10,
                  cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 700
                }}>
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "var(--c-accent)" }}
                  />
                  Cupom ativo
                </label>

                <div style={{
                  display: "flex", justifyContent: "flex-end", gap: 10,
                  marginTop: 26, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,.07)"
                }}>
                  <button type="button" onClick={fecharModal} className="c-btn c-btn--outline" style={{ height: 46 }}>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={salvando}
                    className="c-btn c-btn--primary"
                    style={{ height: 46, display: "flex", alignItems: "center", gap: 8, opacity: salvando ? .6 : 1 }}
                  >
                    <FaSave /> {salvando ? "Salvando..." : "Salvar cupom"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

const actionButton = {
  width: 34,
  height: 34,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 9,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.03)",
  color: "rgba(255,255,255,.68)",
  cursor: "pointer",
}

const closeButton = {
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.03)",
  color: "rgba(255,255,255,.7)",
  cursor: "pointer",
}

const labelStyle = {
  display: "block",
  marginBottom: 7,
  color: "rgba(255,255,255,.72)",
  fontSize: 12,
  fontWeight: 700,
}

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
}
